import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ItemResponse, SubItem } from 'common/types'
import { BetterIDTrayScrew } from 'views/SPDLayout/SPD/SPD.types'
import { extractSubTrayJSONData } from 'lib/utils/extractSubTrayJSONData'
import {
  AssignedDigitalTraysContextProps,
  UsagePlate,
  UsageOtherItem,
  UsageCustomImplant,
} from './AssignedDigitalTrays.types'
import { SubTrayItemProductDetails } from 'lib/services/api/product-service/receiveItems/types'

const AssignedDigitalTraysContext = createContext<
  AssignedDigitalTraysContextProps | undefined
>(undefined)

export const AssignedDigitalTraysProvider = ({
  children,
}: {
  children: JSX.Element
}) => {
  const { surgeryId } = useParams()

  const [trays, setTrays] = useState<ItemResponse[]>([])
  const [selectedTray, setSelectedTray] = useState<ItemResponse | null>(null)
  const [selectedSubTray, setSelectedSubTray] = useState<SubItem | null>(null)
  const [spdScrews, setSpdScrews] = useState<BetterIDTrayScrew[]>([])
  const [usageScrews, setUsageScrews] = useState<BetterIDTrayScrew[]>([])
  const [usagePlates, setUsagePlates] = useState<UsagePlate[]>([])
  const [usageOtherItems, setUsageOtherItems] = useState<UsageOtherItem[]>([])
  const [usageCustomImplants, setUsageCustomImplants] = useState<
    UsageCustomImplant[]
  >([])
  const [trayDetails, setTrayDetails] = useState<
    SubTrayItemProductDetails | undefined
  >(undefined)
  const [hasNurseCapturedTrayImage, setHasNurseCapturedTrayImage] =
    useState(false)

  const defaultUsageHistory = { screws: [], plates: [] }
  const nurseUsageHistory = useMemo(
    () =>
      surgeryId && trayDetails?.nurseUsageHistory
        ? trayDetails.nurseUsageHistory[surgeryId] ?? defaultUsageHistory
        : defaultUsageHistory,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trayDetails, surgeryId]
  )

  useEffect(() => {
    if (selectedSubTray) {
      const productDetails = JSON.parse(
        selectedSubTray.productDetails as string
      )
      setSpdScrews(productDetails?.screws || [])
    } else {
      setSpdScrews([])
    }
  }, [selectedSubTray])

  useEffect(() => {
    if (selectedSubTray) {
      const details = extractSubTrayJSONData(selectedSubTray)
      setTrayDetails(details)
    } else {
      setTrayDetails(undefined)
    }
  }, [selectedSubTray])

  return (
    <AssignedDigitalTraysContext.Provider
      value={{
        trays,
        setTrays,
        selectedTray,
        setSelectedTray,
        selectedSubTray,
        setSelectedSubTray,
        surgeryId: surgeryId as string,
        spdScrews,
        setSpdScrews,
        usageScrews,
        setUsageScrews,
        usagePlates,
        setUsagePlates,
        usageOtherItems,
        setUsageOtherItems,
        trayDetails,
        setTrayDetails,
        hasNurseCapturedTrayImage,
        setHasNurseCapturedTrayImage,
        nurseUsageHistory,
        usageCustomImplants,
        setUsageCustomImplants,
      }}
    >
      {children}
    </AssignedDigitalTraysContext.Provider>
  )
}

export const useAssignedDigitalTrays = () => {
  const context = useContext(AssignedDigitalTraysContext)

  if (context === undefined) {
    throw new Error(
      'useAssignedDigitalTrays must be used with AssignedDigitalTraysProvider'
    )
  }

  return context
}
